// ═══════════════════════════════════════════════════════════════════════
// Écrans du responsable (agent H) — éléments communs : appels à l'API /api/temps/equipe,
// navigation entre « Aujourd'hui », « À traiter » et « Calendrier », fenêtres de décision
// (refus motivé, qualification par la liste du serveur, décision sur un conflit, « Marquer… »).
// Les listes de choix, les actions possibles et les droits viennent TOUJOURS du serveur.
// ═══════════════════════════════════════════════════════════════════════
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CalendarDays, Inbox, UsersRound, X, Check, Send, UserCheck } from 'lucide-react';
import { Bouton, BandeauInfo } from '../../design/composants';
import { Fenetre, Champ, PiedFenetre } from './reglagesCommun';

export const API_EQUIPE = '/api/temps/equipe';
export const lireEquipe = async (url, params) => (await axios.get(`${API_EQUIPE}${url}`, { params })).data.data;
export const ecrireEquipe = async (url, corps) => (await axios.post(`${API_EQUIPE}${url}`, corps || {})).data.data;
export const messageErreur = (e, defaut = 'Opération impossible. Réessayez.') => e?.response?.data?.message || defaut;
export const codeErreur = (e) => e?.response?.data?.code || null;

/** Prévient le menu que le compteur « À traiter » a changé. */
export const signalerATraiter = () => { try { window.dispatchEvent(new Event('sogas:a-traiter')); } catch (_) { /* navigateur ancien */ } };

/** « mardi 6 octobre » (date ISO, sans décalage de fuseau). */
export function jourLong(iso, { annee = false } = {}) {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', ...(annee ? { year: 'numeric' } : {}) });
}
export const aujourdhuiIso = () => {
  const p = new Intl.DateTimeFormat('fr-CA', { timeZone: 'Africa/Dakar', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  return p.slice(0, 10);
};
export const nomComplet = (p) => [p?.prenom, p?.nom].filter(Boolean).join(' ') || 'Salarié';

/** Navigation entre les trois écrans du responsable (onglets-liens). */
export function NavEquipe({ actif, compteur }) {
  const liens = [
    { cle: 'aujourdhui', vers: '/temps/equipe', libelle: 'Aujourd\'hui', Icone: UsersRound },
    { cle: 'a-traiter', vers: '/temps/a-traiter', libelle: compteur ? `À traiter (${compteur})` : 'À traiter', Icone: Inbox },
    { cle: 'calendrier', vers: '/temps/equipe/calendrier', libelle: 'Calendrier', Icone: CalendarDays },
  ];
  return (
    <nav className="rh-onglets" aria-label="Mon équipe">
      {liens.map((l) => (
        <Link key={l.cle} to={l.vers} className="rh-onglet" aria-current={actif === l.cle ? 'page' : undefined}
          style={{ textDecoration: 'none', ...(actif === l.cle ? { borderBottomColor: 'var(--sg-primaire)', color: 'var(--sg-primaire)', fontWeight: 500 } : {}) }}>
          <l.Icone size={15} aria-hidden="true" />{l.libelle}
        </Link>
      ))}
    </nav>
  );
}

/** Droits effectifs renvoyés par le serveur (menu, onglets, secours). */
export function useDroitsEquipe() {
  const [droits, setDroits] = useState(null);
  useEffect(() => {
    let actif = true;
    const charger = () => lireEquipe('/droits').then((d) => { if (actif) setDroits(d); }).catch(() => { if (actif) setDroits({ mon_equipe: false, a_traiter: false }); });
    charger();
    window.addEventListener('sogas:a-traiter', charger);
    return () => { actif = false; window.removeEventListener('sogas:a-traiter', charger); };
  }, []);
  return droits;
}

/** Référentiel de la boîte (libellés des actions, listes contrôlées). */
export function useReferentielATraiter() {
  const [ref, setRef] = useState(null);
  useEffect(() => { let a = true; lireEquipe('/a-traiter/referentiel').then((r) => { if (a) setRef(r); }).catch(() => {}); return () => { a = false; }; }, []);
  return ref;
}

// ─── Refus motivé ────────────────────────────────────────────────────────────────────────────
export function FenetreMotif({ titre, aide, libelleBouton = 'Refuser', variante = 'danger', Icone = X, obligatoire = true, onFermer, onValider }) {
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const valide = !obligatoire || motif.trim().length >= 3;
  const envoyer = async () => {
    setEnvoi(true); setErreur('');
    try { await onValider(motif.trim()); } catch (e) { setErreur(messageErreur(e)); setEnvoi(false); }
  };
  return (
    <Fenetre titre={titre} Icone={Icone} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <Champ libelle="Motif" requis={obligatoire} aide={aide || 'Le motif est communiqué au salarié.'}>
        <textarea className="rh-champ-saisie" rows={3} value={motif} maxLength={1000} onChange={(e) => setMotif(e.target.value)} autoFocus />
      </Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Annuler</Bouton>
        <Bouton variante={variante} Icone={Icone} onClick={envoyer} disabled={!valide || envoi}>{libelleBouton}</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

// ─── Qualification par la liste du serveur (D-26) ────────────────────────────────────────────
/** Champs à saisir pour une qualification ; `champs` vient du serveur. Le congé se rattache depuis l'écran des congés. */
function ChampsQualification({ champs, valeurs, maj, referentiel }) {
  const sel = (k, options) => (
    <select className="rh-champ-saisie" value={valeurs[k] || ''} onChange={(e) => maj(k, e.target.value)}>
      <option value="">Choisir…</option>
      {(options || []).map((o) => <option key={o.code} value={o.code}>{o.libelle}</option>)}
    </select>
  );
  return (
    <>
      {champs.includes('sens') && <Champ libelle="Passage oublié" requis>{sel('sens', referentiel?.sens)}</Champ>}
      {champs.includes('heure') && <Champ libelle="Heure" requis><input type="time" className="rh-champ-saisie" value={valeurs.heure || ''} onChange={(e) => maj('heure', e.target.value)} /></Champ>}
      {champs.includes('heure_arrivee') && (
        <div style={{ display: 'flex', gap: 12 }}>
          <Champ libelle="Arrivée" aide="Vide : début de la plage"><input type="time" className="rh-champ-saisie" value={valeurs.heure_arrivee || ''} onChange={(e) => maj('heure_arrivee', e.target.value)} /></Champ>
          <Champ libelle="Départ" aide="Vide : fin de la plage"><input type="time" className="rh-champ-saisie" value={valeurs.heure_depart || ''} onChange={(e) => maj('heure_depart', e.target.value)} /></Champ>
        </div>
      )}
      {champs.includes('type_permission') && <Champ libelle="Permission" requis>{sel('type_code', referentiel?.types_permission)}</Champ>}
      {champs.includes('motif_permission') && <Champ libelle="Motif de la permission" requis>{sel('motif_code', referentiel?.motifs_permission)}</Champ>}
      {champs.includes('lieu') && <Champ libelle="Lieu de la mission" requis><input className="rh-champ-saisie" value={valeurs.lieu || ''} maxLength={160} onChange={(e) => maj('lieu', e.target.value)} /></Champ>}
      {champs.includes('titre') && <Champ libelle="Intitulé de la formation" requis><input className="rh-champ-saisie" value={valeurs.titre || ''} maxLength={200} onChange={(e) => maj('titre', e.target.value)} /></Champ>}
      {champs.includes('incident') && (
        <div style={{ display: 'flex', gap: 12 }}>
          <Champ libelle="Panne de" requis><input type="time" className="rh-champ-saisie" value={valeurs.incident_debut || ''} onChange={(e) => maj('incident_debut', e.target.value)} /></Champ>
          <Champ libelle="à" requis><input type="time" className="rh-champ-saisie" value={valeurs.incident_fin || ''} onChange={(e) => maj('incident_fin', e.target.value)} /></Champ>
        </div>
      )}
    </>
  );
}

/** Données envoyées au serveur pour une qualification. */
function donneesQualification(champs, v, motif) {
  const d = {};
  if (champs.includes('sens')) d.sens = v.sens;
  if (champs.includes('heure')) d.heure = v.heure;
  if (champs.includes('heure_arrivee')) { if (v.heure_arrivee) d.heure_arrivee = v.heure_arrivee; if (v.heure_depart) d.heure_depart = v.heure_depart; }
  if (champs.includes('type_permission')) d.type_code = v.type_code;
  if (champs.includes('motif_permission')) { d.motif_code = v.motif_code; if (motif) d.motif = motif; }
  if (champs.includes('lieu')) d.lieu = v.lieu;
  if (champs.includes('titre')) d.titre = v.titre;
  if (champs.includes('incident')) d.incident = { heure_debut: v.incident_debut, heure_fin: v.incident_fin, motif: motif || 'Incident de pointage' };
  return d;
}

const champsComplets = (champs, v) => champs.every((c) => ({
  sens: !!v.sens, heure: !!v.heure, heure_arrivee: true, heure_depart: true, type_permission: !!v.type_code, motif_permission: !!v.motif_code,
  lieu: (v.lieu || '').trim().length >= 2, titre: (v.titre || '').trim().length >= 3, incident: !!v.incident_debut && !!v.incident_fin, conge: false,
}[c] ?? true));

/**
 * Régler une anomalie (ou accepter une justification) : la qualification est choisie dans la liste
 * renvoyée par le serveur pour CETTE ligne ; les champs demandés aussi.
 * `onValider({ qualification, motif, donnees })`.
 */
export function FenetreQualifier({ ligne, referentiel, titre = 'Régler', onFermer, onValider }) {
  const qualifications = (ligne.qualifications || []).filter((q) => !(q.champs || []).includes('conge'));
  const [code, setCode] = useState(ligne.qualification_proposee && qualifications.some((q) => q.code === ligne.qualification_proposee) ? ligne.qualification_proposee : '');
  const [v, setV] = useState({});
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const q = qualifications.find((x) => x.code === code);
  const champs = q ? q.champs || [] : [];
  const valide = q && champsComplets(champs, v) && (!q.motif_obligatoire || motif.trim().length >= 3);
  const envoyer = async () => {
    setEnvoi(true); setErreur('');
    try { await onValider({ qualification: q.code, motif: motif.trim() || undefined, donnees: donneesQualification(champs, v, motif.trim()) }); } catch (e) { setErreur(messageErreur(e)); setEnvoi(false); }
  };
  return (
    <Fenetre titre={`${titre} — ${ligne.libelle}`} Icone={Check} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <p className="rh-form-aide" style={{ margin: '0 0 12px' }}>{ligne.detail}</p>
      <div role="radiogroup" aria-label="Que s'est-il passé ?" style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
        {qualifications.map((x) => (
          <label key={x.code} className="rh-carte" style={{ padding: '8px 10px', marginBottom: 0, cursor: 'pointer', borderColor: code === x.code ? 'var(--sg-primaire)' : undefined }}>
            <input type="radio" name="qualification" value={x.code} checked={code === x.code} onChange={() => setCode(x.code)} style={{ marginRight: 8 }} />
            <strong style={{ fontWeight: 500 }}>{x.libelle}</strong>
            <span className="rh-form-aide" style={{ display: 'block', marginLeft: 24 }}>{x.effet}</span>
          </label>
        ))}
      </div>
      {q && <ChampsQualification champs={champs} valeurs={v} maj={(k, val) => setV({ ...v, [k]: val })} referentiel={referentiel} />}
      {q && (
        <Champ libelle="Motif" requis={q.motif_obligatoire} aide={q.motif_obligatoire ? 'Obligatoire : il est communiqué au salarié.' : 'Facultatif.'}>
          <textarea className="rh-champ-saisie" rows={2} value={motif} maxLength={1000} onChange={(e) => setMotif(e.target.value)} />
        </Champ>
      )}
      <PiedFenetre>
        <Bouton onClick={onFermer}>Annuler</Bouton>
        <Bouton variante="primaire" Icone={Check} onClick={envoyer} disabled={!valide || envoi}>Enregistrer</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

/** Pointage pendant une absence autorisée : trois décisions, renvoyées par le serveur ; motif obligatoire. */
export function FenetreArbitrer({ ligne, onFermer, onValider }) {
  const [decision, setDecision] = useState('');
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const envoyer = async () => {
    setEnvoi(true); setErreur('');
    try { await onValider({ decision, motif: motif.trim() }); } catch (e) { setErreur(messageErreur(e)); setEnvoi(false); }
  };
  return (
    <Fenetre titre={`Décider — ${ligne.libelle}`} Icone={Check} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <div role="radiogroup" aria-label="Décision" style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
        {(ligne.decisions || []).map((x) => (
          <label key={x.code} className="rh-carte" style={{ padding: '8px 10px', marginBottom: 0, cursor: 'pointer', borderColor: decision === x.code ? 'var(--sg-primaire)' : undefined }}>
            <input type="radio" name="decision" value={x.code} checked={decision === x.code} onChange={() => setDecision(x.code)} style={{ marginRight: 8 }} />
            <strong style={{ fontWeight: 500 }}>{x.libelle}</strong>
            <span className="rh-form-aide" style={{ display: 'block', marginLeft: 24 }}>{x.effet}</span>
          </label>
        ))}
      </div>
      <Champ libelle="Motif" requis><textarea className="rh-champ-saisie" rows={2} value={motif} maxLength={1000} onChange={(e) => setMotif(e.target.value)} /></Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Annuler</Bouton>
        <Bouton variante="primaire" Icone={Check} onClick={envoyer} disabled={!decision || motif.trim().length < 3 || envoi}>Enregistrer</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

// ─── Marquer… (Mon équipe aujourd'hui, journée d'un collaborateur) ──────────────────────────
/**
 * `options` : { actions, types_absence, motifs_permission } renvoyés par le serveur.
 * Envoie POST /api/temps/equipe/marquer ; `onTermine(message)`.
 */
export function FenetreMarquer({ personne, date, options, onFermer, onTermine }) {
  const [action, setAction] = useState('');
  const [f, setF] = useState({ heure: '', lieu: '', type_code: '', motif_code: '', motif: '' });
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const a = (options?.actions || []).find((x) => x.code === action);
  const type = (options?.types_absence || []).find((t) => t.code === f.type_code);
  const maj = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const complet = a && (
    (a.code === 'ARRIVE_SANS_POINTER' && f.heure)
    || (a.code === 'EN_MISSION' && f.lieu.trim().length >= 2)
    || (a.code === 'ABSENCE_JUSTIFIEE' && type && (type.famille !== 'PERMISSION' || f.motif_code) && (f.motif_code !== 'AUTRE' || f.motif.trim().length >= 3))
    || (a.code === 'ABSENCE_NON_JUSTIFIEE' && f.motif.trim().length >= 3));
  const envoyer = async () => {
    setEnvoi(true); setErreur('');
    try {
      const r = await ecrireEquipe('/marquer', { employe_id: personne.employe_id || personne.id, date, action, ...f, motif: f.motif.trim() || undefined });
      signalerATraiter();
      onTermine(r.message || 'Enregistré.');
    } catch (e) { setErreur(messageErreur(e)); setEnvoi(false); }
  };
  return (
    <Fenetre titre={`Marquer — ${nomComplet(personne)}`} Icone={UserCheck} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <p className="rh-form-aide" style={{ margin: '0 0 12px' }}>Journée du {jourLong(date)}. Votre décision est tracée à votre nom.</p>
      <div role="radiogroup" aria-label="Situation" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {(options?.actions || []).map((x) => (
          <Bouton key={x.code} petit variante={action === x.code ? 'primaire' : 'secondaire'} role="radio" aria-checked={action === x.code} onClick={() => setAction(x.code)}>{x.libelle}</Bouton>
        ))}
      </div>
      {a && <p className="rh-form-aide" style={{ margin: '0 0 12px' }}>{a.aide}</p>}
      {a?.code === 'ARRIVE_SANS_POINTER' && <Champ libelle="Heure d'arrivée" requis><input type="time" className="rh-champ-saisie" value={f.heure} onChange={maj('heure')} /></Champ>}
      {a?.code === 'EN_MISSION' && <Champ libelle="Lieu" requis><input className="rh-champ-saisie" value={f.lieu} maxLength={160} onChange={maj('lieu')} /></Champ>}
      {a?.code === 'ABSENCE_JUSTIFIEE' && (
        <>
          <Champ libelle="Type d'absence" requis>
            <select className="rh-champ-saisie" value={f.type_code} onChange={maj('type_code')}>
              <option value="">Choisir…</option>
              {(options?.types_absence || []).map((t) => <option key={t.code} value={t.code}>{t.libelle}{t.sur_journee_a_qualifier ? ' (journée à qualifier)' : ''}</option>)}
            </select>
          </Champ>
          {type?.famille === 'PERMISSION' && (
            <Champ libelle="Motif de la permission" requis>
              <select className="rh-champ-saisie" value={f.motif_code} onChange={maj('motif_code')}>
                <option value="">Choisir…</option>
                {(options?.motifs_permission || []).map((m) => <option key={m.code} value={m.code}>{m.libelle}</option>)}
              </select>
            </Champ>
          )}
        </>
      )}
      {a && a.code !== 'ARRIVE_SANS_POINTER' && (
        <Champ libelle={a.code === 'ABSENCE_NON_JUSTIFIEE' ? 'Motif' : 'Précision'} requis={a.code === 'ABSENCE_NON_JUSTIFIEE' || f.motif_code === 'AUTRE'}
          aide={a.code === 'ABSENCE_NON_JUSTIFIEE' ? 'Obligatoire : il est notifié au salarié, qui peut le contester.' : 'Facultatif.'}>
          <textarea className="rh-champ-saisie" rows={2} value={f.motif} maxLength={1000} onChange={maj('motif')} />
        </Champ>
      )}
      <PiedFenetre>
        <Bouton onClick={onFermer}>Annuler</Bouton>
        <Bouton variante={a?.code === 'ABSENCE_NON_JUSTIFIEE' ? 'danger' : 'primaire'} Icone={Send} onClick={envoyer} disabled={!complet || envoi}>Enregistrer</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}
